<template>
  <div class="mt-6">
    <div class="border border-gray-200 rounded-lg overflow-hidden text-sm">
      <!-- Header -->
      <div class="flex bg-gray-50 border-b border-gray-200">
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Health & Safety
        </div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">Value</div>
        <div class="w-1/3 px-4 py-3 font-medium text-gray-500">
          Last Updated
        </div>
      </div>
      <!-- Emergency Contact 1 Name -->
      <PlayerInfoRow
        :label="readModel.emergencyContact1Name.label"
        :last-updated="formRows.emergencyContact1Name.lastUpdated"
        :display="readModel.emergencyContact1Name.display"
        :is-editing="isEditing"
        data-testid="emergency-contact-1-name-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.emergencyContact1Name.data"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.emergencyContact1Name.data }}
        </template>
      </PlayerInfoRow>
      <!-- Emergency Contact 1 Email -->
      <PlayerInfoRow
        :label="readModel.emergencyContact1Email.label"
        :last-updated="formRows.emergencyContact1Email.lastUpdated"
        :display="readModel.emergencyContact1Email.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="emergency-contact-1-email-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.emergencyContact1Email.data"
            type="email"
            :invalid="v$.emergencyContact1Email.data.$invalid"
            class="w-full"
            @input="v$.emergencyContact1Email.data.$touch()"
          />
        </template>
        <template #display>
          {{ formRows.emergencyContact1Email.data }}
        </template>
      </PlayerInfoRow>
      <!-- Emergency Contact 1 Phone -->
      <PlayerInfoRow
        :label="readModel.emergencyContact1Phone.label"
        :last-updated="formRows.emergencyContact1Phone.lastUpdated"
        :display="readModel.emergencyContact1Phone.display"
        :is-editing="isEditing"
        data-testid="emergency-contact-1-phone-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <PhoneNumber v-model="formRows.emergencyContact1Phone.data" />
        </template>
        <template #display>
          {{ formatPhoneNumber(formRows.emergencyContact1Phone.data) }}
        </template>
      </PlayerInfoRow>
      <!-- Emergency Contact 2 Name -->
      <PlayerInfoRow
        :label="readModel.emergencyContact2Name.label"
        :last-updated="formRows.emergencyContact2Name.lastUpdated"
        :display="readModel.emergencyContact2Name.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="emergency-contact-2-name-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.emergencyContact2Name.data"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.emergencyContact2Name.data }}
        </template>
      </PlayerInfoRow>
      <!-- Emergency Contact 2 Email -->
      <PlayerInfoRow
        :label="readModel.emergencyContact2Email.label"
        :last-updated="formRows.emergencyContact2Email.lastUpdated"
        :display="readModel.emergencyContact2Email.display"
        :is-editing="isEditing"
        data-testid="emergency-contact-2-email-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.emergencyContact2Email.data"
            type="email"
            :invalid="v$.emergencyContact2Email.data.$invalid"
            class="w-full"
            @input="v$.emergencyContact2Email.data.$touch()"
          />
        </template>
        <template #display>
          {{ formRows.emergencyContact2Email.data }}
        </template>
      </PlayerInfoRow>
      <!-- Emergency Contact 2 Phone -->
      <PlayerInfoRow
        :label="readModel.emergencyContact2Phone.label"
        :last-updated="formRows.emergencyContact2Phone.lastUpdated"
        :display="readModel.emergencyContact2Phone.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="emergency-contact-2-phone-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <PhoneNumber v-model="formRows.emergencyContact2Phone.data" />
        </template>
        <template #display>
          {{ formatPhoneNumber(formRows.emergencyContact2Phone.data) }}
        </template>
      </PlayerInfoRow>
      <!-- Medical Conditions -->
      <PlayerInfoRow
        :label="readModel.medicalConditions.label"
        :last-updated="formRows.medicalConditions.lastUpdated"
        :display="readModel.medicalConditions.display"
        :is-editing="isEditing"
        data-testid="medical-conditions-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText v-model="formRows.medicalConditions.data" class="w-full" />
        </template>
        <template #display>
          {{ formRows.medicalConditions.data }}
        </template>
      </PlayerInfoRow>
      <!-- Allergies -->
      <PlayerInfoRow
        :label="readModel.allergies.label"
        :last-updated="formRows.allergies.lastUpdated"
        :display="readModel.allergies.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="allergies-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText v-model="formRows.allergies.data" class="w-full" />
        </template>
        <template #display>
          {{ formRows.allergies.data }}
        </template>
      </PlayerInfoRow>
      <!-- Dietary Restrictions -->
      <PlayerInfoRow
        :label="readModel.dietaryRestrictions.label"
        :last-updated="formRows.dietaryRestrictions.lastUpdated"
        :display="readModel.dietaryRestrictions.display"
        :is-editing="isEditing"
        data-testid="dietary-restrictions-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText
            v-model="formRows.dietaryRestrictions.data"
            class="w-full"
          />
        </template>
        <template #display>
          {{ formRows.dietaryRestrictions.data }}
        </template>
      </PlayerInfoRow>
      <!-- Current Injuries -->
      <PlayerInfoRow
        :label="readModel.currentInjuries.label"
        :last-updated="formRows.currentInjuries.lastUpdated"
        :display="readModel.currentInjuries.display"
        :is-editing="isEditing"
        :stripe="true"
        data-testid="current-injuries-value"
        @dblclick="$emit('enterEditMode')"
      >
        <template #input>
          <InputText v-model="formRows.currentInjuries.data" class="w-full" />
        </template>
        <template #display>
          {{ formRows.currentInjuries.data }}
        </template>
      </PlayerInfoRow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import InputText from "primevue/inputtext";
import PhoneNumber from "@/shared/components/PhoneNumber.vue";
import PlayerInfoRow from "../PlayerInfoRow.vue";
import { formatPhoneNumber } from "@/shared/utils/phone-number";
import type { HealthSafetyFormRows } from "./types";
import * as filter from "../filter";
import useVuelidate from "@vuelidate/core";
import { email } from "@vuelidate/validators";

const props = defineProps<{
  isEditing: boolean;
  searchQuery: string | null | undefined;
}>();

const formRows = defineModel<HealthSafetyFormRows>("formRows", {
  required: true,
});

defineEmits<{
  enterEditMode: [];
}>();

const validationRules = {
  emergencyContact1Email: { data: { email } },
  emergencyContact2Email: { data: { email } },
};

const v$ = useVuelidate(validationRules, formRows);

const isInvalid = defineModel<boolean>("isInvalid", {
  required: true,
});

watch(v$, (newValue) => {
  isInvalid.value = newValue.$invalid;
});

const readModel = computed(() => {
  return {
    emergencyContact1Name: {
      label: "Emergency Contact 1 Name",
      display: filter.matches(
        "Emergency Contact 1 Name",
        formRows.value?.emergencyContact1Name.data,
        props.searchQuery
      ),
    },
    emergencyContact1Email: {
      label: "Emergency Contact 1 Email",
      display: filter.matches(
        "Emergency Contact 1 Email",
        formRows.value?.emergencyContact1Email.data,
        props.searchQuery
      ),
    },
    emergencyContact1Phone: {
      label: "Emergency Contact 1 Phone",
      display: filter.matches(
        "Emergency Contact 1 Phone",
        formatPhoneNumber(formRows.value?.emergencyContact1Phone.data),
        props.searchQuery
      ),
    },
    emergencyContact2Name: {
      label: "Emergency Contact 2 Name",
      display: filter.matches(
        "Emergency Contact 2 Name",
        formRows.value?.emergencyContact2Name.data,
        props.searchQuery
      ),
    },
    emergencyContact2Email: {
      label: "Emergency Contact 2 Email",
      display: filter.matches(
        "Emergency Contact 2 Email",
        formRows.value?.emergencyContact2Email.data,
        props.searchQuery
      ),
    },
    emergencyContact2Phone: {
      label: "Emergency Contact 2 Phone",
      display: filter.matches(
        "Emergency Contact 2 Phone",
        formatPhoneNumber(formRows.value?.emergencyContact2Phone.data),
        props.searchQuery
      ),
    },
    medicalConditions: {
      label: "Medical Conditions",
      display: filter.matches(
        "Medical Conditions",
        formRows.value?.medicalConditions.data,
        props.searchQuery
      ),
    },
    allergies: {
      label: "Allergies",
      display: filter.matches(
        "Allergies",
        formRows.value?.allergies.data,
        props.searchQuery
      ),
    },
    dietaryRestrictions: {
      label: "Dietary Restrictions",
      display: filter.matches(
        "Dietary Restrictions",
        formRows.value?.dietaryRestrictions.data,
        props.searchQuery
      ),
    },
    currentInjuries: {
      label: "Current Injuries",
      display: filter.matches(
        "Current Injuries",
        formRows.value?.currentInjuries.data,
        props.searchQuery
      ),
    },
  };
});
</script>
